import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import Message from "controls/Message";
import React, { useState } from "react";

const MAX_ROWS = 500;

// Parses a CSV string into { headers, rows }. Handles quoted fields and BOM.
export function parseCsv(text) {
  const cleaned = text
    .replace(/^\uFEFF/, "")
    .replace(/\r\n?/g, "\n")
    .trim();
  const lines = cleaned.split("\n").filter((l) => l.trim() !== "");
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const fields = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' && !inQuotes) {
        inQuotes = true;
      } else if (ch === '"' && inQuotes) {
        inQuotes = false;
      } else if (ch === "," && !inQuotes) {
        fields.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
    fields.push(current.trim());
    return fields;
  };

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map((line) => parseRow(line));
  return { headers, rows };
}

// Serialises headers + rows back to a CSV string.
export function serialiseCsv(headers, rows) {
  const escape = (val) => {
    const s = String(val ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(escape).join(","), ...rows.map((row) => row.map(escape).join(","))];
  return lines.join("\n");
}

// Standard "CSV must include: ..." info line
export function csvRequiredColumnsMessage(headers) {
  return `CSV must include: ${headers.join(", ")}.`;
}

// Returns the required headers that are absent from the uploaded CSV's header row.
export function missingCsvHeaders(requiredHeaders = [], csvHeaders) {
  if (!csvHeaders) return [];
  const present = new Set(csvHeaders.map((h) => h.trim()));
  return requiredHeaders.filter((h) => !present.has(h));
}

// Serialises CSV rows and encodes them as a base64 Uploader payload for import mutations.
export function csvToAssetUploader(headers, rows, name) {
  const csvString = serialiseCsv(headers, rows);
  const bytes = new TextEncoder().encode(csvString);
  const base64 = btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join(""));
  return { name, base64, size: csvString.length };
}

// Converts "29/05/2026 18:00" → "2026-05-29T18:00" for datetime-local input.
function csvToDatetimeLocal(val) {
  const match = val.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}:\d{2})/);
  if (!match) return "";
  const [, dd, mm, yyyy, time] = match;
  return `${yyyy}-${mm}-${dd}T${time}`;
}

// Converts "2026-05-29T18:00" → "29/05/2026 18:00" for CSV storage.
function datetimeLocalToCsv(val) {
  if (!val) return "";
  const match = val.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/);
  if (!match) return val;
  const [, yyyy, mm, dd, time] = match;
  return `${dd}/${mm}/${yyyy} ${time}`;
}

// Converts "29/05/2026" → "2026-05-29" for date input.
function csvToDateInput(val) {
  const match = val.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return "";
  const [, dd, mm, yyyy] = match;
  return `${yyyy}-${mm}-${dd}`;
}

// Converts "2026-05-29" → "29/05/2026" for CSV storage.
function dateInputToCsv(val) {
  if (!val) return "";
  const match = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return val;
  const [, yyyy, mm, dd] = match;
  return `${dd}/${mm}/${yyyy}`;
}

// Per-type native input config: how to convert to/from the CSV value and how to render the input.
const COLUMN_TYPE_CONFIG = {
  datetime: {
    inputType: "datetime-local",
    toInput: csvToDatetimeLocal,
    toCsv: datetimeLocalToCsv,
    minWidth: 220,
    step: 60,
  },
  date: { inputType: "date", toInput: csvToDateInput, toCsv: dateInputToCsv, minWidth: 180 },
  time: { inputType: "time", toInput: (v) => v.trim(), toCsv: (v) => v, minWidth: 150, step: 60 },
};

export default function CsvPreviewTable({
  headers,
  rows,
  onChange,
  columnValidators = {},
  columnTypes = {},
  maxRows = MAX_ROWS,
  // When true, invalid cells are highlighted as warnings (yellow) rather than errors (red)
  validationsAsWarnings = false,
}) {
  const [editingCell, setEditingCell] = useState(null); // { rowIdx, colIdx }
  const [editValue, setEditValue] = useState("");

  const tooManyRows = rows.length > maxRows;

  const startEdit = (rowIdx, colIdx) => {
    setEditingCell({ rowIdx, colIdx });
    const rawValue = rows[rowIdx][colIdx] ?? "";
    const config = COLUMN_TYPE_CONFIG[columnTypes[headers[colIdx]]];
    setEditValue(config ? config.toInput(rawValue) : rawValue);
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const { rowIdx, colIdx } = editingCell;
    const config = COLUMN_TYPE_CONFIG[columnTypes[headers[colIdx]]];
    const finalValue = config ? config.toCsv(editValue) : editValue;
    const newRows = rows.map((r, ri) => (ri === rowIdx ? r.map((c, ci) => (ci === colIdx ? finalValue : c)) : r));
    onChange(newRows);
    setEditingCell(null);
  };

  const handleKeyDown = (e, rowIdx, colIdx) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit();
      if (rowIdx + 1 < rows.length) startEdit(rowIdx + 1, colIdx);
    } else if (e.key === "Tab") {
      e.preventDefault();
      commitEdit();
      const nextCol = colIdx + 1 < headers.length ? colIdx + 1 : 0;
      const nextRow = colIdx + 1 < headers.length ? rowIdx : rowIdx + 1;
      if (nextRow < rows.length) startEdit(nextRow, nextCol);
    } else if (e.key === "Escape") {
      setEditingCell(null);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 4, fontSize: "0.85em", color: "#555" }}>
        {rows.length} row{rows.length !== 1 ? "s" : ""} loaded — click any cell to edit
      </div>

      {tooManyRows && (
        <div style={{ marginBottom: 8 }}>
          <Message
            type="error"
            content={`Too many rows (${rows.length}). Maximum is ${maxRows}. Please split your CSV into smaller files.`}
          />
        </div>
      )}

      <TableContainer component={Paper} variant="outlined" style={{ maxHeight: 400, overflow: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: "bold", background: "#f5f5f5", width: 40 }}>#</TableCell>
              {headers.map((h) => (
                <TableCell key={h} style={{ fontWeight: "bold", background: "#f5f5f5", whiteSpace: "nowrap" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.slice(0, maxRows).map((row, rowIdx) => (
              <TableRow key={rowIdx} hover>
                <TableCell style={{ color: "#999", fontSize: "0.75em" }}>{rowIdx + 1}</TableCell>
                {headers.map((header, colIdx) => {
                  const isEditing = editingCell?.rowIdx === rowIdx && editingCell?.colIdx === colIdx;
                  const cellValue = row[colIdx] ?? "";
                  const validationError = !isEditing ? columnValidators[header]?.(cellValue) : null;
                  const typeConfig = COLUMN_TYPE_CONFIG[columnTypes[header]];

                  const cell = (
                    <TableCell
                      key={colIdx}
                      style={{
                        cursor: "text",
                        whiteSpace: "nowrap",
                        // Same width whether displaying or editing so the cell doesn't shift on click.
                        minWidth: typeConfig?.minWidth ?? 240,
                        background: validationError
                          ? validationsAsWarnings
                            ? "rgba(255,193,7,0.25)"
                            : "rgba(211,47,47,0.08)"
                          : undefined,
                      }}
                      onClick={() => !isEditing && startEdit(rowIdx, colIdx)}
                    >
                      {isEditing ? (
                        <TextField
                          autoFocus
                          size="small"
                          variant="standard"
                          type={typeConfig?.inputType ?? "text"}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={(e) => handleKeyDown(e, rowIdx, colIdx)}
                          fullWidth
                          slotProps={typeConfig?.step ? { htmlInput: { step: typeConfig.step } } : undefined}
                        />
                      ) : (
                        cellValue
                      )}
                    </TableCell>
                  );

                  return validationError ? (
                    <Tooltip key={colIdx} title={validationError} placement="top" arrow>
                      {cell}
                    </Tooltip>
                  ) : (
                    cell
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
